import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';

import { fetchDepartments, updateUser } from 'services/registration';

import { schemaUpdateUser } from './validation';
import { useTranslation } from 'react-i18next';
import { PATHS } from 'constants/routes';
import { useNavigate } from 'react-router-dom';

export interface UpdateUserRequest {
    first_name: string;
    last_name: string;
    phone?: string;
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
    employee_position_name?: string;
    department?: string;
    email: string;
    workplace_id: number;
}

export const useProfilUpdate = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userData = (() => {
        const raw = localStorage.getItem('userData');
        return raw ? JSON.parse(raw) : {};
    })();

    const {
        register: registerUpdate,
        handleSubmit: handleSubmitUpdate,
        formState: { errors: updateErrors },
        setError: setErrorUpdate,
        reset,
        control,
    } = useForm<UpdateUserRequest>({
        resolver: yupResolver(schemaUpdateUser),
        defaultValues: {
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
            phone: userData?.phone || '000000000',
            workplace_name: userData?.workplace_name || '',
            workplace_address_city: userData?.workplace_address_city || '',
            workplace_address_street: userData?.workplace_address_street || '',
            workplace_address_number: userData?.workplace_address_number || '',
            workplace_address_postcode: userData?.workplace_address_postcode || '',
            employee_position_name: userData?.employee_position_name || 'stanowisko',
            department: userData?.department || '',
            email: userData?.email || '',
        },
    });

    const mutationUpdate = useMutation(updateUser, {
        onSuccess: (res) => {
            toast.success(t('registration.successUpdated'));
            reset(res.data);
            navigate(PATHS.HAND_HYGIENE);
        },
        onError: (error: any) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setErrorUpdate(key as keyof UpdateUserRequest, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const { data: departments } = useQuery(
        ['departments', userData?.workplace_id],
        () => fetchDepartments(userData?.workplace_id),
        { enabled: !!userData?.workplace_id }
    );

    const onSubmitUpdate = (data: UpdateUserRequest) => {
        mutationUpdate.mutate(data);
    };

    return {
        registerUpdate,
        handleSubmitUpdate,
        setErrorUpdate,
        onSubmitUpdate,
        updateErrors,
        t,
        control,
        departments
    };
};